import type maplibregl from "maplibre-gl";
import type { Feature, FeatureCollection, LineString, Point } from "geojson";
import type { RoutePoint } from "../routes/routes";
import type { RouteLogSample } from "../routes/routeLogs";

const SOURCE_ID = "klint-route-log";
const LAYER_ID = "klint-route-log-line";
const POINT_LAYER_ID = "klint-route-log-points";
const MAX_VISIBLE_POINTS = 10_000;

interface TrackPoint {
  point: RoutePoint;
  motorOn: boolean;
}

interface TrackProperties {
  motorOn: boolean;
  marker?: "start" | "end" | "single";
}

function trackData(points: TrackPoint[]): FeatureCollection<LineString | Point, TrackProperties> {
  const features: Array<Feature<LineString | Point, TrackProperties>> = [];
  if (points.length > 1) {
    let motorOn = points[1].motorOn;
    let coordinates: RoutePoint[] = [points[0].point, points[1].point];
    for (let index = 2; index < points.length; index++) {
      if (points[index].motorOn === motorOn) {
        coordinates.push(points[index].point);
        continue;
      }
      features.push({
        type: "Feature",
        properties: { motorOn },
        geometry: { type: "LineString", coordinates },
      });
      motorOn = points[index].motorOn;
      coordinates = [points[index - 1].point, points[index].point];
    }
    features.push({
      type: "Feature",
      properties: { motorOn },
      geometry: { type: "LineString", coordinates },
    });
  }
  if (points.length) {
    features.push({
      type: "Feature",
      properties: {
        motorOn: points[0].motorOn,
        marker: points.length === 1 ? "single" : "start",
      },
      geometry: { type: "Point", coordinates: points[0].point },
    });
  }
  if (points.length > 1) {
    const last = points.at(-1)!;
    features.push({
      type: "Feature",
      properties: { motorOn: last.motorOn, marker: "end" },
      geometry: { type: "Point", coordinates: last.point },
    });
  }
  return {
    type: "FeatureCollection",
    features,
  };
}

/** Draws an active recording or a route log loaded from IndexedDB. */
export class RouteLogOverlay {
  private readonly points: TrackPoint[] = [];

  constructor(private readonly map: maplibregl.Map) {}

  start(): void {
    this.clear();
    this.ensureLayer();
  }

  add(point: RoutePoint, motorOn: boolean): void {
    this.points.push({ point, motorOn });
    if (this.points.length > MAX_VISIBLE_POINTS) this.points.shift();
    (this.map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      trackData(this.points),
    );
  }

  show(samples: RouteLogSample[]): void {
    this.points.splice(
      0,
      this.points.length,
      ...samples
        .slice(-MAX_VISIBLE_POINTS)
        .map((sample) => ({
          point: [sample.longitude, sample.latitude] satisfies RoutePoint,
          motorOn: sample.motorOn === true,
        })),
    );
    this.ensureLayer();
    (this.map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(trackData(this.points));
  }

  clear(): void {
    this.points.length = 0;
    if (this.map.getLayer(POINT_LAYER_ID)) this.map.removeLayer(POINT_LAYER_ID);
    if (this.map.getLayer(LAYER_ID)) this.map.removeLayer(LAYER_ID);
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID);
  }

  private ensureLayer(): void {
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, { type: "geojson", data: trackData([]) });
    }
    if (!this.map.getLayer(LAYER_ID)) {
      this.map.addLayer({
        id: LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": [
            "case",
            ["boolean", ["get", "motorOn"], false],
            "#f59e0b",
            "#22d3ee",
          ],
          "line-opacity": 0.95,
          "line-width": 4,
        },
      });
    }
    if (!this.map.getLayer(POINT_LAYER_ID)) {
      this.map.addLayer({
        id: POINT_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        paint: {
          "circle-radius": 5,
          "circle-color": [
            "case",
            ["==", ["get", "marker"], "start"],
            "#22c55e",
            ["==", ["get", "marker"], "end"],
            "#ef4444",
            "#22d3ee",
          ],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });
    }
  }
}
